import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import Axios from "../Common/api/axiosConfig";
import { Box, LinearProgress } from "@mui/material";

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  login: (token: string) => void;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Route helpers matching your project routes
// const isPublicRoute = (path: string) => {
//   const publicPaths = [
//     "/login",
//     "/register",
//     "/forgot-password",
//     "/logout",
//     "/otp-screen",
//     "/create-store",
//   ];

//   // Check exact matches first
//   if (publicPaths.includes(path)) {
//     return true;
//   }

//   // Check if path starts with /view-store/ (dynamic route)
//   if (path.startsWith("/:url") || path === "/view-store") {
//     return true;
//   }

//   return false;
// };

const isPublicRoute = (path: string) => {
  const hostname = window.location.hostname.toLowerCase().replace(/^www\./, "");
  const isStoreDomain =
    hostname !== "app.therudrix.in" &&
    !hostname.startsWith("admin.") &&
    hostname !== "localhost" &&
    hostname !== "127.0.0.1";

  // If accessed from a storefront domain (e.g. therudrix.in), root path is public
  if (isStoreDomain && path === "/") {
    return true;
  }

  const publicPaths = [
    "/login",
    "/register",
    "/forgot-password",
    "/logout",
    "/otp-screen",
    "/create-store",
    "/:url",
  ];

  // Exact matches first
  if (publicPaths.includes(path)) {
    return true;
  }

  // Dynamic single-segment store routes like /anuj-store
  if (/^\/[^/]+$/.test(path) && !publicPaths.includes(path)) {
    return true;
  }

  return false;
};

const isAuthRoute = (path: string) => {
  return path === "/login" || path === "/register";
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [progress, setProgress] = useState(0);
  const [buffer, setBuffer] = useState(10);
  const navigate = useNavigate();
  const location = useLocation();

  // Animate the linear progress bar
  useEffect(() => {
    if (!loading) return;
    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress >= 100) {
          return 0;
        }
        const diff = Math.random() * 10;
        return Math.min(oldProgress + diff, 100);
      });

      setBuffer((oldBuffer) => {
        const diff = Math.random() * 10;
        return Math.min(oldBuffer + diff, 100);
      });
    }, 200);

    return () => clearInterval(timer);
  }, [loading]);

  const logout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userData");
    sessionStorage.clear();
    setToken(null);
    navigate("/login", { replace: true });
  };

  const validateToken = (): string | null => {
    const localToken = localStorage.getItem("authToken");
    if (localToken) {
      try {
        const decoded: any = jwtDecode(localToken);
        const now = Date.now() / 1000;
        if (decoded.exp < now) {
          logout();
          return null;
        } else {
          return localToken;
        }
      } catch {
        logout();
        return null;
      }
    }
    return null;
  };

  // Initial token validation and redirect logic
  useEffect(() => {
    const currentToken = validateToken();
    const currentPath = location.pathname;

    setToken(currentToken);

    if (!currentToken && !isPublicRoute(currentPath)) {
      navigate("/login", { replace: true });
    } else if (currentToken && isAuthRoute(currentPath)) {
      navigate("/dashboard", { replace: true });
    }

    setLoading(false);
    setIsReady(true);
  }, []);

  // Periodic token check
  useEffect(() => {
    if (!isReady) return;
    const interval = setInterval(() => {
      const currentToken = validateToken();
      if (currentToken !== token) {
        setToken(currentToken);
      }
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, [isReady, token]);

  const login = (newToken: string) => {
    localStorage.setItem("authToken", newToken);
    setToken(newToken);
    navigate("/dashboard", { replace: true });
  };

  const isAuthenticated = !!token;

  // Show MUI LinearProgress while checking auth
  if (loading) {
    return (
      <Box
        sx={{ width: "100%", position: "fixed", top: 0, left: 0, zIndex: 2000 }}
      >
        <LinearProgress
          variant="buffer"
          value={progress}
          valueBuffer={buffer}
        />
      </Box>
    );
  }

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, token, login, logout, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
